import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Select, 
  Card, 
  Row, 
  Col, 
  Typography,
  Divider,
  Tag,
  Tooltip,
  Spin
} from 'antd';
import { 
  SwapOutlined, 
  SearchOutlined, 
  CheckOutlined, 
  CloseOutlined,
  InfoCircleOutlined,
  UserOutlined,
  TeamOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { doctorService, Doctor } from '../services/doctorService';
import { volunteerService, Volunteer } from '../services/volunteerService';
import { getProjects, Project } from '../services/project';
import { authService } from '../services/authService';
import axiosInstance from '../utils/axios';
import { useLocation } from 'react-router-dom';
import useResponsive from '../hooks/useResponsive';

const { Option } = Select;
const { Title, Text } = Typography;

interface SwitchState {
  selectedProject: number | null;
  selectedDoctors: number[];
  currentVolunteer: number | null;
  targetVolunteer: number | null;
  switchMode: 'single' | 'batch';
  loading: boolean;
}

interface DoctorWithVolunteer extends Doctor {
  currentVolunteer?: {
    id: number;
    name: string;
    phone: string;
  };
}

const DoctorVolunteerSwitch: React.FC = () => {
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [doctors, setDoctors] = useState<DoctorWithVolunteer[]>([]);
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [volunteerSearchLoading, setVolunteerSearchLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [doctorsPagination, setDoctorsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [doctorSearchValue, setDoctorSearchValue] = useState('');
  const [doctorSearchLoading, setDoctorSearchLoading] = useState(false);
  const [sourceVolunteerSearchValue, setSourceVolunteerSearchValue] = useState('');
  const [sourceVolunteerSearchLoading, setSourceVolunteerSearchLoading] = useState(false);
  const [selectedDoctorsList, setSelectedDoctorsList] = useState<DoctorWithVolunteer[]>([]);
  const [currentSearchType, setCurrentSearchType] = useState<'none' | 'doctor' | 'volunteer'>('none');
  
  const [switchState, setSwitchState] = useState<SwitchState>({
    selectedProject: null,
    selectedDoctors: [],
    currentVolunteer: null,
    targetVolunteer: null,
    switchMode: 'batch',
    loading: false
  });

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmData, setConfirmData] = useState<any>(null);

  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();

  // 获取项目列表
  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      if (response.success) {
        setProjects(response.data);
      }
    } catch (error) {
      message.error('获取项目列表失败');
    }
  };

  // 获取志愿者列表（用于目标志愿者选择）
  const fetchVolunteers = async () => {
    // 不再自动加载全量志愿者，改为搜索模式
    setVolunteers([]);
  };

  // 搜索志愿者（目标志愿者）
  const searchVolunteers = async (searchValue: string) => {
    if (!searchValue || searchValue.length < 2) {
      setVolunteers([]);
      return;
    }

    try {
      setVolunteerSearchLoading(true);
      const response = await volunteerService.getVolunteers({
        keyword: searchValue,
        page: 1,
        pageSize: 50
      });
      if (response.code === 0) {
        setVolunteers(response.data.list);
      }
    } catch (error) {
      message.error('搜索志愿者失败');
    } finally {
      setVolunteerSearchLoading(false);
    }
  };

  // 搜索医生
  const searchDoctors = async (searchValue: string, page?: number, pageSize?: number) => {
    if (!switchState.selectedProject) {
      message.warning('请先选择项目');
      return;
    }

    try {
      setDoctorSearchLoading(true);
      
      if (!searchValue || searchValue.length < 2) {
        // 清空搜索，恢复原始数据
        setCurrentSearchType('none');
        await fetchDoctors(1, 10);
        return;
      }
      
      setCurrentSearchType('doctor');
      
      // 使用新接口搜索医生（包含志愿者信息）
      const response = await volunteerService.getDoctorsWithVolunteers({
        name: searchValue,
        project_id: switchState.selectedProject,
        page: page || 1,
        pageSize: pageSize || 50 // 搜索结果限制在50条以内
      });
      
      if (response.success) {
        setDoctors(response.data);
        // 只在首次搜索时重置分页，分页时保持当前页
        if (!page) {
          setDoctorsPagination({
            current: 1,
            pageSize: 10,
            total: response.total || 0
          });
        } else {
          setDoctorsPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize,
            total: response.total || 0
          }));
        }
        
        if (!page) {
          message.success(`找到 ${response.data.length} 个医生`);
        }
      }
    } catch (error) {
      message.error('搜索医生失败');
    } finally {
      setDoctorSearchLoading(false);
    }
  };

  // 搜索源志愿者
  const searchSourceVolunteers = async (searchValue: string, page?: number, pageSize?: number) => {
    if (!switchState.selectedProject) {
      message.warning('请先选择项目');
      return;
    }

    try {
      setSourceVolunteerSearchLoading(true);
      
      if (!searchValue || searchValue.length < 2) {
        // 清空搜索，恢复原始数据
        setCurrentSearchType('none');
        await fetchDoctors(1, 10);
        return;
      }
      
      setCurrentSearchType('volunteer');
      
      // 使用志愿者接口搜索源志愿者
      const response = await volunteerService.getVolunteers({
        keyword: searchValue,
        project_id: switchState.selectedProject!,
        page: 1,
        pageSize: 50
      });
      
      if (response.code === 0) {
        const sourceVolunteers = response.data.list;
        if (sourceVolunteers.length === 0) {
          message.info('未找到匹配的源志愿者');
          return;
        }

        // 获取源志愿者的ID列表
        const sourceVolunteerIds = sourceVolunteers.map((v: Volunteer) => v.id);
        
        // 先获取所有医生数据，然后筛选出绑定到源志愿者的医生
        const doctorsResponse = await volunteerService.getDoctorsWithVolunteers({
          project_id: switchState.selectedProject!,
          page: 1,
          pageSize: 10000 // 获取所有数据用于前端筛选
        });

        if (doctorsResponse.success) {
          // 筛选出绑定到源志愿者的医生
          const filteredDoctors = doctorsResponse.data.filter((doctor: any) => {
            return doctor.currentVolunteer && sourceVolunteerIds.includes(doctor.currentVolunteer.id);
          });
          
          // 前端分页处理
          const currentPage = page || 1;
          const currentPageSize = pageSize || 10;
          const startIndex = (currentPage - 1) * currentPageSize;
          const endIndex = startIndex + currentPageSize;
          const paginatedDoctors = filteredDoctors.slice(startIndex, endIndex);
          
          setDoctors(paginatedDoctors);
          
          // 设置分页信息
          setDoctorsPagination({
            current: currentPage,
            pageSize: currentPageSize,
            total: filteredDoctors.length
          });
          
          if (!page) {
            message.success(`找到 ${filteredDoctors.length} 个绑定到源志愿者的医生`);
          }
        }
      }
    } catch (error) {
      message.error('搜索源志愿者失败');
    } finally {
      setSourceVolunteerSearchLoading(false);
    }
  };

  // 获取医生列表（带当前绑定信息）
  const fetchDoctors = async (page?: number, pageSize?: number) => {
    if (!switchState.selectedProject) return;

    try {
      setLoading(true);
      
      const currentPage = page || doctorsPagination.current;
      const currentPageSize = pageSize || doctorsPagination.pageSize;
      
      // 使用新接口获取医生列表（包含志愿者信息）
      const response = await volunteerService.getDoctorsWithVolunteers({
        project_id: switchState.selectedProject!,
        page: currentPage,
        pageSize: currentPageSize
      });
      
      if (response.success) {
        setDoctors(response.data);
        setDoctorsPagination(prev => ({
          ...prev,
          current: currentPage,
          pageSize: currentPageSize,
          total: response.total || 0
        }));
      }
    } catch (error) {
      message.error('获取医生列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 项目选择变化
  const handleProjectChange = (projectId: number) => {
    setSwitchState(prev => ({
      ...prev,
      selectedProject: projectId,
      selectedDoctors: [],
      currentVolunteer: null,
      targetVolunteer: null
    }));
    // 重置分页
    setDoctorsPagination({
      current: 1,
      pageSize: 10,
      total: 0
    });
  };

  // 医生选择变化
  const handleDoctorSelectionChange = (selectedRowKeys: React.Key[]) => {
    const selectedDoctorIds = selectedRowKeys.map(key => Number(key));
    setSwitchState(prev => ({
      ...prev,
      selectedDoctors: selectedDoctorIds,
      switchMode: selectedDoctorIds.length === 1 ? 'single' : 'batch'
    }));
    
    // 更新已选择的医生列表
    const selectedDoctors = doctors.filter(doctor => selectedDoctorIds.includes(doctor.id));
    setSelectedDoctorsList(selectedDoctors);
  };

  // 目标志愿者选择变化
  const handleTargetVolunteerChange = (volunteerId: number) => {
    setSwitchState(prev => ({
      ...prev,
      targetVolunteer: volunteerId
    }));
  };

  // 确认切换
  const handleConfirmSwitch = () => {
    if (!switchState.selectedProject || !switchState.targetVolunteer || switchState.selectedDoctors.length === 0) {
      message.warning('请选择项目、医生和目标志愿者');
      return;
    }

    // 准备确认数据
    const selectedDoctorList = doctors.filter(d => switchState.selectedDoctors.includes(d.id));
    const targetVolunteerInfo = volunteers.find(v => v.id === switchState.targetVolunteer);
    
    setConfirmData({
      doctors: selectedDoctorList,
      targetVolunteer: targetVolunteerInfo,
      project: projects.find(p => p.id === switchState.selectedProject)
    });
    
    setConfirmModalVisible(true);
  };

  // 执行切换
  const executeSwitch = async () => {
    try {
      setSwitchLoading(true);
      
      const response = await axiosInstance.post('/volunteers/switch-doctor-volunteers', {
        doctor_ids: switchState.selectedDoctors,
        to_volunteer_id: switchState.targetVolunteer,
        project_id: switchState.selectedProject
      });

      if (response.data.success) {
        const { data } = response.data;
        message.success(`切换成功！更新了 ${data.binding_count} 个绑定关系，${data.submission_count} 个问卷提交记录，${data.labor_detail_count} 个劳务明细记录`);
        
        // 重置状态
        setSwitchState(prev => ({
          ...prev,
          selectedDoctors: [],
          targetVolunteer: null
        }));
        
        // 刷新医生列表，保持当前搜索状态
        await refreshDoctorsList();
      } else {
        message.error(response.data.message || '切换失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '切换失败，请重试');
    } finally {
      setSwitchLoading(false);
      setConfirmModalVisible(false);
    }
  };

  // 表格列配置
  const columns: ColumnsType<DoctorWithVolunteer> = [
    {
      title: '医生信息',
      key: 'doctor',
      render: (_, record) => (
        <div>
          <div><strong>{record.name}</strong></div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.hospitalName} - {record.phone}
          </div>
        </div>
      ),
    },
    {
      title: '当前绑定志愿者',
      key: 'currentVolunteer',
      render: (_, record) => {
        if (record.currentVolunteer) {
          return (
            <div>
              <Tag color="blue">
                {record.currentVolunteer.name}
              </Tag>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {record.currentVolunteer.phone}
              </div>
            </div>
          );
        }
        return <Tag color="red">未绑定</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setSwitchState(prev => ({
              ...prev,
              selectedDoctors: [record.id],
              switchMode: 'single'
            }));
          }}
        >
          选择切换
        </Button>
      ),
    },
  ];

  // 初始化数据
  useEffect(() => {
    fetchProjects();
    fetchVolunteers();
    
    // 处理从其他页面传递过来的状态
    if (location.state) {
      const { mode, doctorId, doctorName, volunteerId, volunteerName, projectId } = location.state;
      
      if (mode === 'single') {
        if (doctorId) {
          // 从医生管理页面跳转
          setSwitchState(prev => ({
            ...prev,
            selectedProject: projectId || null,
            selectedDoctors: [doctorId],
            switchMode: 'single'
          }));
        } else if (volunteerId) {
          // 从志愿者管理页面跳转
          setSwitchState(prev => ({
            ...prev,
            selectedProject: projectId || null,
            switchMode: 'single'
          }));
        }
      }
    }
  }, [location.state]);

  // 项目变化时重新获取医生
  useEffect(() => {
    if (switchState.selectedProject) {
      fetchDoctors();
    }
  }, [switchState.selectedProject]);

  // 刷新医生列表，保持当前搜索状态
  const refreshDoctorsList = async () => {
    // 重置分页到第一页
    setDoctorsPagination(prev => ({
      ...prev,
      current: 1
    }));
    
    // 根据当前搜索状态决定调用哪个函数
    switch (currentSearchType) {
      case 'doctor':
        await searchDoctors(doctorSearchValue, 1, doctorsPagination.pageSize);
        break;
      case 'volunteer':
        await searchSourceVolunteers(sourceVolunteerSearchValue, 1, doctorsPagination.pageSize);
        break;
      default:
        await fetchDoctors(1, doctorsPagination.pageSize);
    }
  };

  // 分页变化时重新获取医生
  const handlePaginationChange = (page: number, pageSize?: number) => {
    // 根据当前搜索状态决定调用哪个函数
    switch (currentSearchType) {
      case 'doctor':
        searchDoctors(doctorSearchValue, page, pageSize);
        break;
      case 'volunteer':
        searchSourceVolunteers(sourceVolunteerSearchValue, page, pageSize);
        break;
      default:
        fetchDoctors(page, pageSize);
    }
  };

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      <Title level={isMobile ? 4 : 2}>
        <SwapOutlined /> 医生志愿者切换管理
      </Title>

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={isMobile ? 24 : 6}>
            <Form.Item label="选择项目" required>
              <Select
                style={{ width: '100%' }}
                placeholder="请选择项目"
                value={switchState.selectedProject}
                onChange={handleProjectChange}
              >
                {projects.map(project => (
                  <Option key={project.id} value={project.id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 6}>
            <Form.Item label="医生搜索">
              <Input
                placeholder="输入医生姓名搜索"
                value={doctorSearchValue}
                onChange={(e) => setDoctorSearchValue(e.target.value)}
                onPressEnter={() => searchDoctors(doctorSearchValue)}
                suffix={
                  <Space>
                    {doctorSearchValue && (
                      <CloseOutlined 
                        onClick={() => {
                          setDoctorSearchValue('');
                          fetchDoctors();
                        }}
                        style={{ cursor: 'pointer', color: '#999' }}
                      />
                    )}
                    <SearchOutlined 
                      onClick={() => searchDoctors(doctorSearchValue)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Space>
                }
              />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 6}>
            <Form.Item label="源志愿者搜索">
              <Input
                placeholder="输入源志愿者姓名搜索"
                value={sourceVolunteerSearchValue}
                onChange={(e) => setSourceVolunteerSearchValue(e.target.value)}
                onPressEnter={() => searchSourceVolunteers(sourceVolunteerSearchValue)}
                suffix={
                  <Space>
                    {sourceVolunteerSearchValue && (
                      <CloseOutlined 
                        onClick={() => {
                          setSourceVolunteerSearchValue('');
                          fetchDoctors();
                        }}
                        style={{ cursor: 'pointer', color: '#999' }}
                      />
                    )}
                    <SearchOutlined 
                      onClick={() => searchSourceVolunteers(sourceVolunteerSearchValue)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Space>
                }
              />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 6}>
            <Form.Item label="目标志愿者" required>
              <Select
                style={{ width: '100%' }}
                placeholder="请输入志愿者姓名搜索"
                value={switchState.targetVolunteer}
                onChange={handleTargetVolunteerChange}
                allowClear
                showSearch
                loading={volunteerSearchLoading}
                filterOption={false}
                onSearch={searchVolunteers}
                notFoundContent={
                  volunteerSearchLoading 
                    ? '搜索中...' 
                    : volunteers.length === 0 
                      ? '请输入至少2个字符开始搜索'
                      : '未找到匹配的志愿者'
                }
              >
                {volunteers.map(volunteer => (
                  <Option key={volunteer.id} value={volunteer.id}>
                    {volunteer.name} ({volunteer.phone})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="操作">
              <Space>
                <Button
                  type="primary"
                  icon={<SwapOutlined />}
                  onClick={handleConfirmSwitch}
                  disabled={!switchState.selectedProject || !switchState.targetVolunteer || switchState.selectedDoctors.length === 0}
                  loading={switchLoading}
                >
                  确认切换
                </Button>
                <Button
                  onClick={() => {
                    setSwitchState(prev => ({
                      ...prev,
                      selectedDoctors: [],
                      targetVolunteer: null
                    }));
                    setSelectedDoctorsList([]);
                    setDoctorSearchValue('');
                    setSourceVolunteerSearchValue('');
                  }}
                >
                  清空选择
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 已选择的医生列表 */}
      {selectedDoctorsList.length > 0 && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>
              <UserOutlined /> 已选择的医生 ({selectedDoctorsList.length}个)
            </Text>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <Row gutter={[8, 8]}>
              {selectedDoctorsList.map((doctor) => (
                <Col key={doctor.id} span={isMobile ? 24 : 8}>
                  <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                    <div>
                      <div><strong>{doctor.name}</strong></div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {doctor.hospitalName} - {doctor.phone}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        当前绑定：{doctor.currentVolunteer?.name || '未绑定'}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Card>
      )}

      {switchState.selectedProject && (
        <Card>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>医生列表</Text>
            {switchState.selectedDoctors.length > 0 && (
              <Tag color="green" style={{ marginLeft: '8px' }}>
                已选择 {switchState.selectedDoctors.length} 个医生
              </Tag>
            )}
          </div>
          
          <Table
            columns={columns}
            dataSource={doctors}
            rowKey="id"
            loading={loading}
            rowSelection={{
              selectedRowKeys: switchState.selectedDoctors,
              onChange: handleDoctorSelectionChange,
              type: 'checkbox'
            }}
            pagination={{
              current: doctorsPagination.current,
              pageSize: doctorsPagination.pageSize,
              total: doctorsPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: handlePaginationChange,
              onShowSizeChange: handlePaginationChange
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}

      {/* 确认切换弹窗 */}
      <Modal
        title="确认切换操作"
        open={confirmModalVisible}
        onOk={executeSwitch}
        onCancel={() => setConfirmModalVisible(false)}
        confirmLoading={switchLoading}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        {confirmData && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>切换详情：</Text>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <Text strong>项目：</Text>
              <Tag color="blue">{confirmData.project?.name}</Tag>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <Text strong>目标志愿者：</Text>
              <Tag color="green">{confirmData.targetVolunteer?.name}</Tag>
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <Text strong>待切换医生（{confirmData.doctors.length}个）：</Text>
            </div>
            
            <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '16px' }}>
              {confirmData.doctors.map((doctor: DoctorWithVolunteer) => (
                <div key={doctor.id} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <div><strong>{doctor.name}</strong></div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {doctor.hospitalName} - {doctor.phone}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    当前绑定：{doctor.currentVolunteer?.name || '未绑定'}
                  </div>
                </div>
              ))}
            </div>
            
            <div style={{ backgroundColor: '#fff7e6', padding: '12px', borderRadius: '4px', border: '1px solid #ffd591' }}>
              <InfoCircleOutlined style={{ color: '#fa8c16', marginRight: '8px' }} />
              <Text style={{ color: '#fa8c16' }}>
                此操作将更新相关的绑定关系、问卷提交记录和劳务明细记录，请确认无误后继续。
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DoctorVolunteerSwitch;
